import { Button, Checkbox, Form, FormProps, Input, Modal, ModalProps, Radio, Select, Spin } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import patterns from '../../config/patterns';
import jobsService from '../../services/jobs.service';
import './dashboard.css';
import { TADatePicker, TASelect, TATimePicker } from './styles';
import { InterviewSlotDto } from '../../types';
import moment from 'moment';

const { Option } = Select;

interface InterviewSlotFormModalProps {
	slot?: InterviewSlotDto;
	allowExtraSlot?: boolean;
	jobs?: any;
	job?: any;
	onSlotFormCancel?: () => void;
	onCreateSlotSuccess?: (response: any, extraSlot: boolean) => void;
	onCreateSlotError?: (e: any) => void;
	onEditSlotSuccess?: (slot) => void;
	onEditSlotError?: (e: any) => void;
	visible: boolean;
}

export const InterviewSlotFormModal: React.FC<ModalProps & FormProps & InterviewSlotFormModalProps> = props => {
	const [companyLocation, setCompanyLocation] = useState<string>();
	const [interviewMethod, setInterviewMethod] = useState<'online' | 'offline'>();
	const [loading, setLoading] = useState<boolean>(false);
	const [extraSlot, setExtraSlot] = useState<boolean>(false);

	const [form] = Form.useForm();

	const mountLogic = async () => {
		const res: any = await jobsService.getCurrentCompany();
		const location = [res.location.city, res.location.country, res.location.area]
			.filter(e => e !== undefined)
			.join(', ');
		setCompanyLocation(location);
	};

	useEffect(() => {
		mountLogic();
	}, []);

	useEffect(() => {
		form.setFieldsValue({
			date: props.slot?.appointment?.from ? dayjs(props.slot?.appointment?.from) : undefined,
			appointment: {
				from: props.slot?.appointment?.from ? dayjs(props.slot?.appointment?.from) : undefined,
				to: props.slot?.appointment?.to ? dayjs(props.slot?.appointment?.to) : undefined
			},
			job: props.job?.status !== 'Closed' && props.job?.id,
			interviewer: {
				name: props.slot?.interviewer?.name,
				email: props.slot?.interviewer?.email
			},
			location: props.slot?.location,
			notes: props.slot?.notes,
			interviewMethod: props.slot?.hasOwnProperty('isOnline')
				? props.slot.isOnline
					? 'online'
					: 'offline'
				: null
		});
	}, [props.slot, props.job]);

	const constructDateAndTime = (date, startTime, endTime) => {
		const from = moment(date.format('DD-MM-YYYY') + ' ' + startTime.format('HH:mm:ss'), 'DD/MM/YYYY HH:mm');
		const to = moment(date.format('DD-MM-YYYY') + ' ' + endTime.format('HH:mm:ss'), 'DD/MM/YYYY HH:mm');
		return { from, to };
	};

	const validateTimeFields = () => {
		if (form.isFieldTouched(['appointment', 'from'])) {
			form.validateFields([['appointment', 'from']]);
		}
		if (form.isFieldTouched(['appointment', 'to'])) {
			form.validateFields([['appointment', 'to']]);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		props.onSlotFormCancel && props.onSlotFormCancel();
	};

	const onOk = () => {
		setExtraSlot(form.getFieldValue('extraSlot'));
		form.submit();
	};

	const onFinish = async values => {
		const slotData = {
			location: values.location,
			interviewer: values.interviewer,
			appointment: {
				from: constructDateAndTime(values.date, values.appointment.from, values.appointment.to).from,
				to: constructDateAndTime(values.date, values.appointment.from, values.appointment.to).to
			},
			isOnline: interviewMethod === 'online',
			job: values.job,
			notes: values.notes
		};

		let response;
		try {
			setLoading(true);
			if (props.slot?.id) response = await jobsService.editInterviewSlot(props.slot.id, slotData);
			else response = await jobsService.addInterviewSlot(slotData.job, slotData);
			//check to see if backend responded with success.
			if (!response) throw new Error('Something went wrong');
		} catch (e) {
			props.onCreateSlotError && props.onCreateSlotError(e);
			props.onEditSlotError && props.onEditSlotError(e);
			return;
		} finally {
			setLoading(false);
		}
		props.onCreateSlotSuccess && props.onCreateSlotSuccess(response, extraSlot);
		props.onEditSlotSuccess && props.onEditSlotSuccess({ ...slotData, id: props.slot?.id });
		if (extraSlot)
			form.resetFields([['appointment', 'from'], ['appointment', 'to'], 'interviewMethod', 'location']);
		else handleCancel();
	};

	return (
		<Modal
			title={`Interview slot details`}
			visible={props.visible}
			onCancel={handleCancel}
			footer={[
				<Button onClick={props.onSlotFormCancel}>Cancel</Button>,
				<Button type="primary" onClick={onOk}>
					Submit
				</Button>
			]}
			className="ta-modal"
		>
			<Spin spinning={loading}>
				<Form
					scrollToFirstError
					name="interviewSlot"
					form={form}
					layout="vertical"
					className="ta-form"
					onFinish={onFinish}
				>
					<Form.Item
						name="date"
						label="Date"
						rules={[
							{ required: true, message: 'Please add interview date!' },
							{
								validator: (rule, value, callback) => {
									if (value && value.isBefore(dayjs().startOf('day'))) {
										callback("Date can't be before today");
									}
									callback();
								}
							}
						]}
					>
						<TADatePicker style={{ width: '100%' }} />
					</Form.Item>

					<div className="d-flex justify-content-between">
						<Form.Item
							name={['appointment', 'from']}
							label="Start Time"
							rules={[{ required: true, message: 'Please add interview start time!' }]}
							wrapperCol={{ span: 23 }}
							className="d-inline-block w-100"
						>
							<TATimePicker
								style={{ width: '100%' }}
								format="HH:mm"
								minuteStep={15}
								secondStep={61}
								use12Hours
								onSelect={time => {
									form.setFieldsValue({
										appointment: { ...form.getFieldValue('appointment'), from: time }
									});
									validateTimeFields();
								}}
							/>
						</Form.Item>
						<Form.Item
							name={['appointment', 'to']}
							label="End Time"
							rules={[
								{ required: true, message: 'Please add interview end time!' },
								{
									validator: (rule, value, callback) => {
										const startTime = form.getFieldValue(['appointment', 'from']);
										if (
											value &&
											startTime &&
											!value.isAfter(startTime, 'hour') &&
											!value.isAfter(startTime, 'minute')
										) {
											callback('End time must be after Start time');
										}
										callback();
									}
								},
								{
									validator: (rule, value, callback) => {
										let startTime = form.getFieldValue(['appointment', 'from']);
										if (startTime) startTime = startTime.clone().add(1, 'hours');
										if (
											value &&
											startTime &&
											(startTime.isBefore(value, 'hour') || startTime.isBefore(value, 'minute'))
										) {
											callback('Maximum Interview Time is 1 hour');
										}
										callback();
									}
								}
							]}
							wrapperCol={{ span: 23, offset: 1 }}
							className="d-inline-block w-100"
						>
							<TATimePicker
								style={{ width: '100%' }}
								format="HH:mm"
								minuteStep={15}
								secondStep={61}
								use12Hours
								onSelect={time => {
									form.setFieldsValue({
										appointment: { ...form.getFieldValue('appointment'), to: time }
									});
									validateTimeFields();
								}}
							/>
						</Form.Item>
					</div>
					<Form.Item
						label="Job"
						name="job"
						rules={[{ required: true, message: 'Please specify a job!' }]}
						extra={props.job && props.job.status !== 'Closed' ? '' : 'Select from open jobs'}
					>
						<TASelect
							disabled={props.job}
							placeholder="Select a job"
							showSearch
							filterOption={(input, option) =>
								option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							className="ta-ant-input"
						>
							{props.jobs.map(
								job =>
									job.status !== 'Closed' && (
										<Option value={job.id} key={job.id}>
											{job.title}
										</Option>
									)
							)}
						</TASelect>
					</Form.Item>
					<Form.Item
						label="Interviewer Name"
						name={['interviewer', 'name']}
						rules={[
							{ required: true, message: "You must specify the interviewer's name." },
							{ pattern: patterns.spacedNames, message: 'Not valid name' }
						]}
					>
						<Input />
					</Form.Item>
					<Form.Item
						label="Interviewer Email"
						name={['interviewer', 'email']}
						rules={[
							{ type: 'email', message: 'Not valid email' },
							{ required: true, message: "You must specify the interviewer's email." }
						]}
					>
						<Input />
					</Form.Item>
					<Form.Item
						label="Interview Method"
						name="interviewMethod"
						rules={[{ required: true, message: 'Please specify the interview method' }]}
					>
						<Radio.Group
							onChange={e => {
								const value = e.target.value;
								setInterviewMethod(value);
								form.setFieldsValue({ location: value === 'offline' ? companyLocation : null });
							}}
						>
							<Radio value="online">Online</Radio>
							<Radio value="offline">Offline</Radio>
						</Radio.Group>
					</Form.Item>
					<Form.Item
						name="location"
						label={interviewMethod === 'online' ? 'Interview Link' : 'Interview Location'}
						// rules={[
						// 	{
						// 		required: true,
						// 		message: `You must specify the interview ${
						// 			interviewMethod === 'online' ? 'link' : 'location'
						// 		}`
						// 	}
						// ]}
					>
						<Input
							placeholder={
								interviewMethod === 'online'
									? 'Online interview link'
									: 'Detailed address or Google maps link.'
							}
						/>
					</Form.Item>
					<Form.Item name="notes" label="Notes">
						<Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} />
					</Form.Item>
					{props.allowExtraSlot && (
						<Form.Item name="extraSlot">
							<Checkbox onChange={e => form.setFieldsValue({ extraSlot: e.target.checked })}>
								Add another slot
							</Checkbox>
						</Form.Item>
					)}
				</Form>
			</Spin>
		</Modal>
	);
};
